import { createMemo, Show } from 'solid-js';
import ItemStackView from './ItemStackView';
import MinecraftText from './MinecraftText';
import SearchableItemPicker, { Highlight, type PickerEmptyOption } from './SearchableItemPicker';
import { useVillagerOfferText } from './VillagerOfferPicker';
import { useTranslation } from '../hooks/useTranslation';
import { fromItemStackDto } from '../services/itemRefAdapters';
import { stripColorCodes } from '../services/itemDisplayName';
import { parseSnbt } from '../services/snbtReader';
import type { ItemStackDto, SellbotOfferDto } from '../api/generated/schemas';
import menuStyles from './StorageSelect.module.css';
import styles from './VillagerOfferPicker.module.css';

export default function SellbotOfferPicker(props: {
    offers: SellbotOfferDto[];
    value: string;
    onChange: (offerId: string) => void;
    placeholder: string;
    emptyOption?: PickerEmptyOption;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const text = useVillagerOfferText();

    const offersByName = createMemo(() =>
        [...props.offers].sort((left, right) => text.itemLabel(left.item).localeCompare(text.itemLabel(right.item))));

    return (
        <SearchableItemPicker
            choices={offersByName()}
            keyOf={offer => offer.id}
            searchTextOf={offer => `${text.itemLabel(offer.item)} ${offer.note ?? ''}`}
            value={props.value}
            onChange={props.onChange}
            placeholder={props.placeholder}
            searchPlaceholder={t('SELLBOT_OFFERS_SEARCH_PLACEHOLDER')}
            noMatchKey="SELLBOT_OFFERS_NO_MATCH"
            emptyOption={props.emptyOption}
            disabled={props.disabled}
            renderSelected={offer => (
                <>
                    <ItemStackView stack={fromItemStackDto(offer.item)} size={22} />
                    <span class={menuStyles.name}><OfferName item={offer.item} label={text.itemLabel(offer.item)} query="" /></span>
                </>
            )}
            renderOption={(offer, query) => (
                <>
                    <span class={styles.result}><ItemStackView stack={fromItemStackDto(offer.item)} size={30} showTooltip /></span>
                    <span class={menuStyles.body}>
                        <span class={menuStyles.lineTop}>
                            <span class={menuStyles.name}><OfferName item={offer.item} label={text.itemLabel(offer.item)} query={query()} /></span>
                        </span>
                        <Show when={offer.note}>
                            {(note) => (
                                <span class={menuStyles.lineBottom}>
                                    <span class={menuStyles.stats}><Highlight text={note()} query={query()} /></span>
                                </span>
                            )}
                        </Show>
                    </span>
                </>
            )}
        />
    );
}

// Custom names keep their in-game colours, which Highlight would flatten to plain text.
function OfferName(props: { item: ItemStackDto; label: string; query: string }) {
    const customName = () => parseSnbt(props.item.snbt ?? undefined).displayName;
    return (
        <Show when={customName()} fallback={<Highlight text={props.label} query={props.query} />}>
            {(raw) => <><MinecraftText text={raw()} />{props.label.slice(stripColorCodes(raw()).length)}</>}
        </Show>
    );
}
